import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import classNames from 'classnames'
import { withRouter } from 'next/router'
import tokens from '@mch-group/design-tokens/build/js/design_tokens-module'

import { mq, px2rem } from '@utils'
import { catalogEntityUrl } from '@constants/catalog'
import VirtualGrid from '@components/VirtualGrid'
import GalleryItem from '../Galleries/GalleryItem'
import ArtistItem from '../Artists/ArtistItem'
import ArtworkItem from '../Artworks/ArtworkItem'
import { Col, Container, Row } from '@mch-group/uikit-components'

const mID = 'otm-entity-grid'

const OtmEntityGrid = React.forwardRef(
  ({ entities: { catalogData }, notFoundLabel, className, sortBy, query, hide, labels, router }, ref) => {
    let currentLetter = ''
    const queryData = query.slug || router.asPath
    // there are no items
    if (catalogData.items.length === 0) {
      return <h3>{notFoundLabel}</h3>
    }

    return (
      <Container className={classNames(className, `${mID}__list`)}>
        <Row className={`${hide ? `${mID}__hide` : ''}`}>
          <Col sm={12}>
            <div className={`${mID}__anchor`}>
              <div id={mID} ref={ref} />
            </div>
          </Col>
          {queryData === catalogEntityUrl.artworks && (
            <Col sm={12}>
              {catalogData.items[0].images && (
                <VirtualGrid
                  items={catalogData.items}
                  renderItem={item => <ArtworkItem {...item} imageType='masonry' pageType='catalogue' />}
                  groupBy={sortBy === 'sortingName' ? 'firstLetter' : ''}
                />
              )}
            </Col>
          )}
          {queryData !== catalogEntityUrl.artworks && (
            <>
              {catalogData.items.map(item => {
                if (sortBy === 'sortingName' && currentLetter !== item.firstLetter) {
                  currentLetter = item.firstLetter
                  return (
                    <Fragment key={item.id}>
                      <Col sm={12} className={`${mID}__column`}>
                        <h2 className={`${mID}__first-letter`}>
                          {item.firstLetter}
                        </h2>
                      </Col>
                      <Col md={3} className={`${mID}__column`}>
                        {(queryData === catalogEntityUrl.galleries)
                         && (
                         <GalleryItem
                           id={item.id}
                           cleanTitle={item.cleanTitle}
                           displayName={item.displayName}
                           showNames={item.showNames}
                           imageUrl={item.imageUrl}
                           withAddToCollection
                           showShowsData
                           labels={{
                            imageAlt: labels?.generalViewGallery,
                            addBtn: labels?.addToCollection,
                            ariaLabel: labels?.generalReadMoreAbout
                          }}
                         />
                        )}
                        {queryData === catalogEntityUrl.artists && (
                          <ArtistItem
                            id={item.id}
                            cleanTitle={item.cleanTitle}
                            name={item.name}
                            imageUrl={item.imageUrl}
                            withAddToCollection
                            labels={{
                              imageAlt: labels?.generalReadMoreArtist,
                              addBtn: labels?.addToCollection,
                              ariaLabel: labels?.generalReadMoreAbout
                            }}
                          />
                        )}
                      </Col>
                    </Fragment>
                  )
                }
                return (
                  <Col md={3} key={item.id} className={`${mID}__column`}>
                    {(queryData === catalogEntityUrl.galleries)
                    && (
                      <GalleryItem
                        id={item.id}
                        cleanTitle={item.cleanTitle}
                        displayName={item.displayName}
                        showNames={item.showNames}
                        imageUrl={item.imageUrl}
                        withAddToCollection
                        showShowsData
                        showSeperator
                        labels={{
                          imageAlt: labels?.generalViewGallery,
                          addBtn: labels?.addToCollection,
                          ariaLabel: labels?.generalReadMoreAbout
                        }}
                      />
                    )}
                    {queryData === catalogEntityUrl.artists && (
                      <ArtistItem
                        id={item.id}
                        cleanTitle={item.cleanTitle}
                        name={item.name}
                        imageUrl={item.imageUrl}
                        withAddToCollection
                        labels={{
                          imageAlt: labels?.generalReadMoreArtist,
                          addBtn: labels?.addToCollection,
                          ariaLabel: labels?.generalReadMoreAbout
                        }}
                      />
                    )}
                  </Col>
                )
              })}
            </>
          )}
        </Row>
      </Container>
    )
  }
)
OtmEntityGrid.displayName = 'OtmEntityGrid'

OtmEntityGrid.propTypes = {
  className: PropTypes.string,
  sortBy: PropTypes.string,
  entities: PropTypes.object,
  notFoundLabel: PropTypes.string,
  query: PropTypes.object,
  hide: PropTypes.bool,
  router: PropTypes.object
}

const StyledEntityGrid = styled(OtmEntityGrid)`
  &&& {
    height: 100%;

    .${mID} {
      &__hide {
        display: none;
      }

      &__anchor {
        position: relative;

        #${mID} {
          position: absolute;
          left: 0;
          ${mq.lessThan('largestTablet')`
            top: -${MFP.vars.siteHeaderHeight};
          `}
          ${mq.greaterThan('desktop')`
            top: -${px2rem(80)};
          `}
        }
      }

      &__column {
        margin-bottom: ${px2rem(40)} !important;
        ${mq.lessThan('largestTablet')`
          margin-bottom: 30px !important;
        `}
      }

      &__first-letter {
        ${mq.lessThan('largestTablet')`
          padding-bottom: ${px2rem(15)} !important;
          margin-bottom: ${px2rem(20)} !important;
          border-bottom: ${px2rem(1)} solid ${tokens.color.light.base.neutrals['300'].value};
        `}
      }
    }
  }
`

export default withRouter(StyledEntityGrid)
